/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { IcPlus, IcStar, IcDots, IcEye, IcEdit, IcCopy, IcTrash, IcDashboard, IcAlert, IcTv } from "./icons";
import { Skeleton } from "../../../components/shared/Loading";

const rel = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const badgeFor = (mode) => {
  if (mode === "public") return { cls: "public", label: "Public" };
  if (mode === "admins") return { cls: "admins", label: "Admins" };
  if (mode === "shared" || mode === "workspaces" || mode === "players") {
    return { cls: "workspaces", label: "Shared" };
  }
  return { cls: "private", label: "Private" };
};

/* Tiny preview of a dashboard's layout inside the card thumbnail. */
function Thumb({ widgets }) {
  const list = (widgets || []).slice(0, 7);
  return (
    <div className="db-thumb">
      {list.map((w) => (
        <span
          key={`${w.metric || "w"}-${w.x}-${w.y}-${w.w}`}
          className="mini"
          style={{
            left: `${((w.x || 0) / 12) * 100}%`,
            width: `${((w.w || 3) / 12) * 100}%`,
            top: 8 + (w.y || 0) * 9,
            height: Math.max(14, (w.h || 3) * 9 - 6),
            transform: "scale(0.92)",
          }}
        />
      ))}
      {!list.length && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--db-muted)", fontSize: 12 }}>
          Empty dashboard
        </div>
      )}
    </div>
  );
}
Thumb.propTypes = { widgets: PropTypes.array };

export default function DashboardsHome({
  dashboards,
  loading,
  currentUserId,
  isAdmin,
  onOpen,
  onCreate,
  onDuplicate,
  onRename,
  onDelete,
  onSetDefault,
  onToggleTv,
}) {
  const [tab, setTab] = useState("mine");
  const [menu, setMenu] = useState(null); // {id, top, left}
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const popRef = useRef(null);
  const hasLocalBoards = dashboards.some((d) => String(d._id || "").startsWith("local_"));

  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      setMenu(null);
    };
    // Fixed menu pinned to the row's open-time coords: close on any scroll
    // (capture:true also catches inner scrollers) or resize so it can't strand.
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === "Escape") setMenu(null); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);

  const mine = dashboards.filter((d) => String(d.ownerId) === String(currentUserId));
  const shared = dashboards.filter(
    (d) =>
      String(d.ownerId) !== String(currentUserId) &&
      (d.visibility?.mode === "shared"
        || d.visibility?.mode === "workspaces"
        || d.visibility?.mode === "players"
        || d.visibility?.mode === "admins")
  );
  const company = dashboards.filter((d) => d.visibility?.mode === "public");
  let list = company;
  if (tab === "mine") list = mine;
  else if (tab === "shared") list = shared;

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenu({ id, top: r.bottom + 6, left: Math.min(r.left - 150, window.innerWidth - 210) });
  };

  const startRename = (d) => {
    setRenamingId(d._id);
    setRenameVal(d.name || "");
    setMenu(null);
  };
  const commitRename = () => {
    if (renamingId && renameVal.trim()) onRename(renamingId, renameVal.trim());
    setRenamingId(null);
  };

  const menuDash = menu ? dashboards.find((d) => d._id === menu.id) : null;

  return (
    <div>
      <div className="db-mobile-note">
        <IcAlert size={16} />
        <span>You&apos;re viewing dashboards in read-only mode on mobile. Switch to a desktop browser to create, rename, duplicate, or delete a dashboard.</span>
      </div>
      <div className="db-home-bar">
        <div className="db-home-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "mine"} onClick={() => setTab("mine")}>My dashboards ({mine.length})</button>
          <button type="button" role="tab" aria-selected={tab === "shared"} onClick={() => setTab("shared")}>Shared with me ({shared.length})</button>
          <button type="button" role="tab" aria-selected={tab === "company"} onClick={() => setTab("company")}>Company ({company.length})</button>
        </div>
      </div>

      {hasLocalBoards && (
        <div className="db-mobile-note" style={{ marginBottom: 12 }}>
          <IcAlert size={16} />
          <span>
            Some dashboards are saved only in this browser (the server API was unavailable when they were created).
            They appear here on this URL only — open the same browser where you built them, or recreate them after the API is deployed.
          </span>
        </div>
      )}

      {(() => {
        if (loading) return (
          <div className="db-grid">
            {[0, 1, 2, 3].map((i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="db-card" style={{ cursor: "default" }}>
                <Skeleton variant="rect" height={90} radius={0} />
                <div className="db-card-body">
                  <Skeleton variant="rect" height={14} width="70%" radius={4} />
                  <Skeleton variant="rect" height={10} width="45%" radius={4} />
                </div>
              </div>
            ))}
          </div>
        );
        if (list.length === 0 && tab === "mine") return (
        <div className="db-empty">
          <div className="art">
            <IcDashboard size={40} />
          </div>
          <h3>Create your first dashboard</h3>
          <p>Pull together the metrics you care about into one view — revenue, deals, activity — as numbers, charts, or tables.</p>
          <button type="button" className="db-btn primary db-desktop-only" onClick={() => onCreate("blank")} style={{ margin: "0 auto" }}>
            <IcPlus />
            Create dashboard
          </button>
        </div>
        );
        return (
        <div className="db-grid">
          {list.map((d) => {
            const badge = badgeFor(d.visibility?.mode);
            return (
              <div key={d._id} className="db-card" onClick={() => (renamingId === d._id ? null : onOpen(d))} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(d)}>
                <Thumb widgets={d.widgets} />
                <div className="db-card-body">
                  <div className="db-card-title">
                    {d.isDefault && (
                      <span className="db-star" title="Default dashboard">
                        <IcStar size={15} />
                      </span>
                    )}
                    {renamingId === d._id ? (
                      <input
                        className="db-input"
                        style={{ height: 30, fontSize: 14 }}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        value={renameVal}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenameVal(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingId(null); }}
                      />
                    ) : (
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                    )}
                  </div>
                  <div className="db-card-meta">
                    <span className={`db-badge ${badge.cls}`}><i />{badge.label}</span>
                    {d.showOnTv && (
                      <span className="db-badge ontv"><IcTv size={11} />On TV</span>
                    )}
                    <span className="db-card-sub">
                      {(d.widgets || []).length} widget{(d.widgets || []).length === 1 ? "" : "s"} · {rel(d.updatedAt)}
                    </span>
                  </div>
                </div>
                {String(d.ownerId) === String(currentUserId) && (
                  <button
                    type="button"
                    className="db-iconbtn db-desktop-only"
                    style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,.9)", boxShadow: "0 1px 4px rgba(16,24,40,.12)" }}
                    onClick={(e) => openMenu(e, d._id)}
                    aria-label="Options"
                  >
                    <IcDots />
                  </button>
                )}
              </div>
            );
          })}
          {tab === "mine" && (
            <button type="button" className="db-card add db-desktop-only" onClick={() => onCreate("blank")}>
              <span className="plus"><IcPlus size={20} /></span>
              New dashboard
            </button>
          )}
        </div>
        );
      })()}

      {menu && menuDash && (
        <div ref={popRef} className="db-pop" style={{ top: menu.top, left: menu.left }}>
          <button type="button" onClick={() => { onOpen(menuDash); setMenu(null); }}>
            <IcEye />
            Open
          </button>
          <button type="button" onClick={() => startRename(menuDash)}>
            <IcEdit />
            Rename
          </button>
          <button type="button" onClick={() => { onDuplicate(menuDash); setMenu(null); }}>
            <IcCopy />
            Duplicate
          </button>
          <button type="button" onClick={() => { onSetDefault(menuDash._id); setMenu(null); }}>
            <IcStar />
            {menuDash.isDefault ? "Default dashboard" : "Set as default"}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                onToggleTv(menuDash);
                setMenu(null);
              }}
            >
              <IcTv />
              {menuDash.showOnTv ? "Remove from TV" : "Show on TV"}
            </button>
          )}
          <div className="sep" />
          <button type="button" className="danger" onClick={() => { onDelete(menuDash._id); setMenu(null); }}>
            <IcTrash />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

DashboardsHome.propTypes = {
  dashboards: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  currentUserId: PropTypes.string,
  isAdmin: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  onToggleTv: PropTypes.func.isRequired,
};
